import Analytics from '../utils/Analytics';
import validateEmail from '../utils/validateEmail';
import * as Sentry from '@sentry/react';
import Environment from '../utils/Environment';
import {PaymentMethod, PaymentMethodCreateParams, Stripe, StripeCardElement} from '@stripe/stripe-js';
import {getUserCountry} from '../utils/locale';

export const BASE_URL = 'https://api.risesci.com';

// This defaults to "test"
const environment = Environment.getVar('REACT_APP_ENV') ?? 'test';

const paymentFailureMessage = 'We were unable to complete your payment. Please try a different payment method.';

/**
 * Create an account with only an email address.
 *
 * @param email - The email address to create an account for.
 * @returns The result of the account creation.
 */
export const createWebAccount = async (
  email: string,
): Promise<{person_id: number; token: string; error: false} | {personId: null; error: true; errorMessage: string}> => {
  const endpoint = `${BASE_URL}/api/web-create-account`;

  Analytics.track('Insomnia Clinic Account Creation Started', {email});

  try {
    if (!email) {
      throw new Error('Email is required');
    }

    if (!validateEmail(email)) {
      throw new Error('Email is invalid');
    }
    const headers = new Headers();
    headers.append('Content-Type', 'application/json');

    const response = await fetch(endpoint, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        email,
        marketingSource: {
          code: 'rise-clinic-intake',
        },
      }),
    });
    const responsePayload = await response.json();

    if (responsePayload.error && responsePayload.error.length) {
      throw new Error(responsePayload.error);
    }

    if (response.status !== 200) {
      throw new Error('Failed to create account');
    }
    console.log('STRIPE: responsePayload', responsePayload);
    const {person_id, token} = responsePayload;

    Analytics.identify(person_id, {email});
    Analytics.track('Insomnia Clinic Account Creation Success', {email});
    return {person_id, token, error: false};
  } catch (error) {
    Analytics.track('Insomnia Clinic Account Creation Failure', {error, email});
    return {personId: null, error: true, errorMessage: (error as Error).message};
  }
};
/**
 * Create a Stripe setup intent for the $0.50 card verification hold for Rise Medical Group.
 *
 * @param email - The user's email address
 * @param name - The user's full name
 * @returns The client secret for the setup intent
 */
export const createClinicCardHold = async (
  email: string,
  name: string,
  person_id: number,
  token: string,
): Promise<{client_secret: string | undefined; authError: boolean}> => {
  const endpoint = `${BASE_URL}/api/stripe-rise-medical-group-charge`;

  Analytics.track('Insomnia Clinic Card Hold Started', {email});

  try {
    const headers = new Headers();
    headers.append('Content-Type', 'application/json');
    headers.append('Authorization', `Bearer ${token}`);

    const response = await fetch(endpoint, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        env: environment === 'production' ? 'production' : 'test',
        email,
        name,
        person_id,
      }),
    });

    if (response.status === 401 || response.status === 403) {
      console.log('STRIPE: auth error, token may be expired', response.status);
      Analytics.track('Insomnia Clinic Card Hold Auth Error', {email, status: response.status});
      return {client_secret: undefined, authError: true};
    }

    if (!response.ok) {
      console.log('STRIPE: response error', response);
      throw new Error(`Failed to create card hold: ${response.status}`);
    }

    const result = await response.json();
    console.log('STRIPE: response', result);
    Analytics.track('Insomnia Clinic Card Hold Success', {email});
    return {...result, authError: false};
  } catch (error) {
    Sentry.captureException(error);
    console.log('STRIPE: response error', error);
    Analytics.track('Insomnia Clinic Card Hold Error', {error, email});
    return {client_secret: undefined, authError: false};
  }
};

/**
 * Perform a payment transaction with a credit card. Will return the id of a subscription to be used in the
 * account creation step or a user-facing error message.
 *
 * @async
 * @param {PlanInfo} plan - The plan associated with the payment.
 * @param {StripeCardElement | null} cardElement - The Stripe card element for card input.
 * @param {Stripe} stripe - The Stripe object for handling payments.
 * @param {string} clientSecret - The client secret associated with the transaction.
 * @returns {Promise<{subscription: string | null; error: string | null}>} - The result of the payment transaction, including the subscription ID and any user-facing error message.
 */
export const payClinicWithCreditCard = async (
  billingDetails: PaymentMethodCreateParams.BillingDetails | null,
  cardElement: StripeCardElement | null,
  paymentMethod: PaymentMethod | null,
  stripe: Stripe | null,
  clientSecret: string,
): Promise<{subscription: string | null; error: string | null}> => {
  const {countryCode: supportedCountry, countryByTimezone} = getUserCountry();

  Analytics.track('Clinic Credit Card Payment Started', {supportedCountry, countryByTimezone});

  // const {result, error} = await validateCreditCard(clientSecret, stripe, elements);

  if (((!cardElement || !billingDetails) && !paymentMethod) || !stripe) {
    return {subscription: null, error: paymentFailureMessage};
  }

  const confirmResult = await stripe.confirmCardPayment(clientSecret, {
    payment_method:
      cardElement && billingDetails
        ? {
            card: cardElement,
            billing_details: billingDetails,
          }
        : paymentMethod?.id,
    return_url: `${window.location.origin}/checkout/success`,
  });

  if (confirmResult?.paymentIntent && !confirmResult.error) {
    Analytics.track(`Clinic Credit Card Success`, {supportedCountry, countryByTimezone});
    //reportMarketingPurchaseEvent(plan);
    return {subscription: confirmResult.paymentIntent.id, error: null};
  } else if (confirmResult?.error) {
    Analytics.track(`Clinic Credit Card Failure`, {error: confirmResult.error, supportedCountry, countryByTimezone});
    return {
      subscription: null,
      error: paymentFailureMessage,
    };
  }

  // if error is present, Analytics will be reported in validateCreditCard method
  Analytics.track(`Clinic Credit Card Failure`, {
    error: 'payment_method missing',
    supportedCountry,
    countryByTimezone,
  });

  return {subscription: null, error: paymentFailureMessage};
};

/**
 * Create an ANONYMOUS RISE account via the same endpoint Nyx-iOS hits
 * at app launch (`/api/create-account` with NO email). This matches the
 * iOS pattern:
 *
 *   app launch → anonymous create-account (no email)
 *   → user goes through onboarding under this anon person_id
 *   → paywall finalize succeeds
 *   → /api/update-email binds the email to the same person_id
 *
 * Critical: passing the email at create-account time produces an
 * account hypnos's GLP-1 entitlement code treats as "never properly
 * signed up" and rejects with "Could not grant entitlement". Sending
 * NO email (just timezone) creates the same anonymous user iOS does,
 * which the entitlement code accepts.
 */
export const createGlp1Account = async (): Promise<
  {person_id: number; token: string; error: false} | {error: true; errorMessage: string}
> => {
  const endpoint = `${BASE_URL}/api/create-account`;

  Analytics.track('GLP-1 Paywall Account Creation Started');

  try {
    const headers = new Headers();
    headers.append('Content-Type', 'application/json; charset=utf-8');

    const response = await fetch(endpoint, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      }),
    });

    if (!response.ok) {
      const body = await response.json().catch(() => ({}));
      const errorMessage = body?.error || `HTTP ${response.status}`;
      Analytics.track('GLP-1 Paywall Account Creation Failure', {status: response.status, error: errorMessage});
      return {error: true, errorMessage};
    }

    const {person_id, token} = await response.json();
    Analytics.track('GLP-1 Paywall Account Creation Success', {person_id});
    return {person_id, token, error: false};
  } catch (error) {
    Sentry.captureException(error);
    Analytics.track('GLP-1 Paywall Account Creation Error', {error: (error as Error).message});
    return {error: true, errorMessage: (error as Error).message};
  }
};

/**
 * Attach an email to an existing (anonymous) hypnos person_id. Called
 * AFTER /stripe-glp1-finalize succeeds, per the iOS pattern. Without
 * this the backend treats the user as anonymous forever.
 *
 * If another user already has this email, hypnos returns 400 — we log
 * the error and let the caller fail-soft (the charge already cleared,
 * we don't want to block the post-purchase flow).
 */
export const updateGlp1Email = async (
  email: string,
  token: string,
): Promise<{ok: true; email: string} | {ok: false; errorMessage: string}> => {
  const endpoint = `${BASE_URL}/api/update-email`;
  try {
    if (!validateEmail(email)) return {ok: false, errorMessage: 'Email is invalid'};
    const headers = new Headers();
    headers.append('Content-Type', 'application/json; charset=utf-8');
    headers.append('Authorization', `Bearer ${token}`);

    const response = await fetch(endpoint, {
      method: 'POST',
      headers,
      body: JSON.stringify({email}),
    });

    if (!response.ok) {
      const body = await response.json().catch(() => ({}));
      return {ok: false, errorMessage: body?.error || `HTTP ${response.status}`};
    }

    const {email: confirmedEmail} = await response.json();
    // Anonymous identify — set the email attribute WITHOUT setting a
    // person id. The payload is the first arg for this overload; passing
    // a first arg of `undefined` would route through the "has person id"
    // branch's negation and silently drop the {email} payload.
    Analytics.identify({email: confirmedEmail});
    return {ok: true, email: confirmedEmail};
  } catch (error) {
    Sentry.captureException(error);
    return {ok: false, errorMessage: (error as Error).message};
  }
};

/* ─────────────────────────────────────────────────────────────────────────────
 * GLP-1 paywall — SetupIntent + finalize flow.
 *
 * The web port of `Hypnos.stripeGLP1Setup` + `Hypnos.stripeGLP1Finalize`
 * (see iOS `useGlp1PaywallPurchase.ts` + the GLP1_FAKE_DOOR_FE_INTEGRATION
 * spec). Two-step charge:
 *
 *   1. /api/stripe-glp1-setup — returns a Stripe SetupIntent client_secret
 *      so the client can confirm the user's card (or Apple Pay) without
 *      charging.
 *   2. /api/stripe-glp1-finalize — off-session charge against the saved
 *      payment method. Hypnos grants entitlement + refunds inline.
 *
 * Pricing ($69 first month membership) lives server-side; the client only
 * passes the variant + email + setup-intent id. ────────────────────────── */

export type Glp1Variant = 'branded' | 'compounded';

export type Glp1SetupResponse = {
  client_secret: string;
  setup_intent_id: string;
  /** Echoed back by Hypnos for analytics; the server also stamps this on
   *  the SetupIntent metadata so /finalize can read it without trusting
   *  the client. */
  variant: Glp1Variant;
};

export type Glp1FinalizeResponse = {
  /** Stripe PaymentIntent id of the $69 off-session charge. */
  payment_intent_id: string;
  /** ISO timestamp the entitlement was granted at. */
  granted_at: string;
};

/**
 * Create a SetupIntent for the GLP-1 paywall.
 *
 * Server-side: stamps the variant on the SetupIntent's metadata so the
 * /finalize endpoint can read it (we never trust a client-passed variant
 * at finalize time).
 */
export const createGlp1Setup = async (
  variant: Glp1Variant,
  personId: number,
  token: string,
): Promise<{ok: true; data: Glp1SetupResponse} | {ok: false; status: number; errorMessage: string}> => {
  const endpoint = `${BASE_URL}/api/stripe-glp1-setup`;

  Analytics.track('GLP-1 Paywall Setup Started', {variant, person_id: personId});

  try {
    const headers = new Headers();
    headers.append('Content-Type', 'application/json');
    headers.append('Authorization', `Bearer ${token}`);

    const response = await fetch(endpoint, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        env: environment === 'production' ? 'production' : 'test',
        variant,
        person_id: personId,
      }),
    });

    if (!response.ok) {
      const body = await response.text().catch(() => '');
      Analytics.track('GLP-1 Paywall Setup Failed', {variant, status: response.status});
      return {ok: false, status: response.status, errorMessage: body || `HTTP ${response.status}`};
    }

    const data = (await response.json()) as Glp1SetupResponse;
    Analytics.track('GLP-1 Paywall Setup Success', {variant, person_id: personId});
    return {ok: true, data};
  } catch (error) {
    Sentry.captureException(error);
    Analytics.track('GLP-1 Paywall Setup Error', {variant, error: (error as Error).message});
    return {ok: false, status: 0, errorMessage: (error as Error).message};
  }
};

/**
 * Finalize the GLP-1 paywall purchase: charges off-session against the
 * saved payment method, grants the user entitlement, and (per spec)
 * refunds inline.
 *
 * `idempotencyKey` should be reused across retries of the same attempt
 * (network / 5xx) and regenerated for a fresh user-initiated attempt.
 */
export const finalizeGlp1 = async ({
  setupIntentId,
  email,
  idempotencyKey,
  personId,
  token,
}: {
  setupIntentId: string;
  email: string;
  idempotencyKey: string;
  personId: number;
  token: string;
}): Promise<
  | {ok: true; data: Glp1FinalizeResponse}
  | {ok: false; status: number; errorMessage: string; chargeMayHaveSucceeded: boolean}
> => {
  const endpoint = `${BASE_URL}/api/stripe-glp1-finalize`;

  Analytics.track('GLP-1 Paywall Finalize Started', {email});

  try {
    const headers = new Headers();
    headers.append('Content-Type', 'application/json');
    headers.append('Authorization', `Bearer ${token}`);

    // Body shape mirrors Nyx-iOS `Hypnos.stripeGLP1Finalize` exactly:
    // {person_id, env, setup_intent_id, email, idempotency_key}.
    // The Idempotency-Key request header is NOT used — hypnos CORS
    // preflight doesn't allowlist it and the backend reads the value
    // from the body before forwarding to Stripe.
    const response = await fetch(endpoint, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        person_id: personId,
        env: environment === 'production' ? 'production' : 'test',
        setup_intent_id: setupIntentId,
        email,
        idempotency_key: idempotencyKey,
      }),
    });

    if (!response.ok) {
      const text = await response.text().catch(() => '');
      // Parse JSON if possible so callers can inspect structured error
      // fields (e.g. {error: 'Could not grant entitlement'}). Per
      // Hypnos spec a 5xx body with an `error` field generally means
      // the charge cleared but a downstream step failed — surface that
      // distinction to the caller via the `chargeMayHaveSucceeded`
      // flag instead of just a generic message.
      let parsed: {error?: string; [key: string]: unknown} | null = null;
      try {
        parsed = text ? JSON.parse(text) : null;
      } catch {
        /* leave parsed as null */
      }
      const errorMessage = parsed?.error ?? (text || `HTTP ${response.status}`);
      const chargeMayHaveSucceeded =
        response.status >= 500 && !!parsed && typeof parsed === 'object' && 'error' in parsed;
      // Dev-time console log so we can see the exact status + body
      // shape on the Netlify preview without backend log access.
      // eslint-disable-next-line no-console
      console.error('[Glp1Paywall] /stripe-glp1-finalize failed', {
        status: response.status,
        body: parsed ?? text,
        setupIntentId,
        personId,
        email,
        chargeMayHaveSucceeded,
      });
      Analytics.track('GLP-1 Paywall Finalize Failed', {
        email,
        status: response.status,
        errorMessage,
        chargeMayHaveSucceeded,
      });
      return {ok: false, status: response.status, errorMessage, chargeMayHaveSucceeded};
    }

    const data = (await response.json()) as Glp1FinalizeResponse;
    Analytics.track('GLP-1 Paywall Finalize Success', {email, personId});
    return {ok: true, data};
  } catch (error) {
    Sentry.captureException(error);
    Analytics.track('GLP-1 Paywall Finalize Error', {email, error: (error as Error).message});
    return {ok: false, status: 0, errorMessage: (error as Error).message, chargeMayHaveSucceeded: false};
  }
};
